import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { useAuthStore } from "@/stores/auth";

// Zmiana hasła — domyślnie WYŁĄCZONA (wejście przez Pilota/SSO). Ekran wraca tylko przy buildzie
// z VITE_PASSWORD_LOGIN=1 (spójne z LoginView + backendowym CRM_PASSWORD_LOGIN). Brak trasy →
// bezpośredni URL /ustawienia/haslo trafia w catch-all (redirect /szanse).
const PASSWORD_LOGIN = (import.meta.env.VITE_PASSWORD_LOGIN as string) === "1";

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/LoginView.vue"),
    meta: { public: true },
  },
  {
    path: "/",
    component: () => import("@/components/layout/AppShell.vue"),
    children: [
      { path: "", redirect: "/szanse" },
      // noAssistant: moduły o pieniądzach/strukturze doradcy — asystentka trafia na swoje zlecenia
      // (backend i tak odpowiada 403, lib/permissions.ts; tu tylko, żeby nie oglądała pustych ekranów).
      { path: "dashboard", name: "dashboard", component: () => import("@/views/DashboardView.vue"), meta: { noAssistant: true } },
      // Zlecenia dla asystentek: asystentka widzi przekazane jej, doradca — przekazane przez siebie.
      { path: "zlecenia", name: "zlecenia", component: () => import("@/views/HandoversView.vue") },

      // Prospekty (cold-calling) — natywna zakładka CRM (decyzja Marcina 2026-06-20)
      { path: "prospekty", name: "prospekty", component: () => import("@/views/ProspektyView.vue") },

      // Szanse (Leads)
      { path: "szanse", name: "szanse", component: () => import("@/views/LeadListView.vue") },
      { path: "szanse/nowa", name: "szansa-nowa", component: () => import("@/views/LeadNewView.vue") },
      { path: "szanse/:id", name: "szansa-detail", component: () => import("@/views/LeadDetailView.vue"), props: true },
      // Cele krótkoterminowe Kompasu na osobnej stronie; na karcie tylko podgląd (decyzja Marcina 2026-10-25).
      { path: "szanse/:id/cele", name: "szansa-cele", component: () => import("@/views/LeadKompasGoalsView.vue"), props: true },
      { path: "kanban/:pipelineId", name: "kanban", component: () => import("@/views/KanbanView.vue"), props: true },

      // Polisy (B1)
      { path: "polisy", name: "polisy", component: () => import("@/views/PoliciesListView.vue") },
      { path: "polisy/nowa", name: "polisa-nowa", component: () => import("@/views/PolicyNewView.vue") },
      { path: "polisy/:id", name: "polisa-detail", component: () => import("@/views/PolicyDetailView.vue"), props: true },

      // Rodziny / Households (B2)
      { path: "rodziny", name: "rodziny", component: () => import("@/views/HouseholdsListView.vue") },
      { path: "rodziny/nowa", name: "rodzina-nowa", component: () => import("@/views/HouseholdNewView.vue") },
      { path: "rodziny/:id", name: "rodzina-detail", component: () => import("@/views/HouseholdDetailView.vue"), props: true },

      // Prowizje (B3)
      { path: "prowizje", name: "prowizje", component: () => import("@/views/CommissionView.vue"), meta: { noAssistant: true } },

      // Oferty (Tor B)
      { path: "oferty", name: "oferty", component: () => import("@/views/OffersListView.vue") },
      { path: "oferty/:id", name: "oferta-builder", component: () => import("@/views/OfferBuilderView.vue"), props: true },
      // Oferta oszczędności (kind='oszczednosci') — osobny edytor (kwoty + scenariusze zamiast bloków).
      { path: "oferty/:id/oszczednosci", name: "oferta-oszczednosci", component: () => import("@/views/SavingsOfferBuilderView.vue"), props: true },
      // Karta rekrutacyjna (kind='rekrutacja') — segment + tonacja RISK zamiast bloków; nie wycenia leada.
      { path: "oferty/:id/rekrutacja", name: "oferta-rekrutacja", component: () => import("@/views/RecruitOfferBuilderView.vue"), props: true },
      // Broszury marketingowe produktów (drukowane z danymi zalogowanego doradcy).
      { path: "broszury", name: "broszury", component: () => import("@/views/BrochuresView.vue") },

      // Osoby / Firmy (B5)
      { path: "osoby", name: "osoby", component: () => import("@/views/PersonsListView.vue") },
      { path: "osoby/nowa", name: "osoba-nowa", component: () => import("@/views/PersonNewView.vue") },
      { path: "osoby/:id", name: "osoba-detail", component: () => import("@/views/PersonDetailView.vue"), props: true },
      { path: "firmy", name: "firmy", component: () => import("@/views/OrganizationsListView.vue") },
      { path: "firmy/:id", name: "firma-detail", component: () => import("@/views/OrganizationDetailView.vue"), props: true },

      // Eventy (szkolenia w strukturze) — docs/DOMAIN.md §19, formularz tworzenia/edycji: v2
      // Mój Kompas osoby rozwijanej (W-013) — także asystentka, jeśli opiekun połączył z nią szansę.
      { path: "moj-kompas", name: "moj-kompas", component: () => import("@/views/MyKompasView.vue") },
      { path: "kompas", name: "kompas", component: () => import("@/views/KompasOverviewView.vue"), meta: { noAssistant: true } },
      { path: "eventy", name: "eventy", component: () => import("@/views/EventsView.vue"), meta: { noAssistant: true } },
      { path: "eventy/nowy", name: "eventy-nowy", component: () => import("@/views/EventFormView.vue"), meta: { noAssistant: true } },
      { path: "eventy/:id/edytuj", name: "eventy-edytuj", component: () => import("@/views/EventFormView.vue"), props: true, meta: { noAssistant: true } },

      // Aktywności
      { path: "aktywnosci", name: "aktywnosci", component: () => import("@/views/ActivitiesInboxView.vue") },
      // Kalendarz (R5) — domyślny kalendarz Google doradcy, renderowany ze zdarzeń z Pilota.
      { path: "kalendarz", name: "kalendarz", component: () => import("@/views/CalendarView.vue") },
      // K6: /zadania → kalendarz z otwartym panelem zadań Google (stary alias inboxu aktywności
      // przeniesiony świadomie; inbox dalej pod /aktywnosci).
      { path: "zadania", redirect: { name: "kalendarz", query: { zadania: "1" } } },
      { path: "pliki", redirect: { name: "hierarchia" } },

      // Ustawienia
      { path: "ustawienia", redirect: "/ustawienia/profil" },
      { path: "ustawienia/profil", name: "settings-profil", component: () => import("@/views/SettingsProfileView.vue") },
      ...(PASSWORD_LOGIN
        ? [{ path: "ustawienia/haslo", name: "settings-haslo", component: () => import("@/views/SettingsPasswordView.vue") }]
        : []),
      // Wniosek o nominację — formularz z danymi z CRM, druk w osobnym widoku (decyzja 2026-10-27).
      { path: "awans/wniosek", name: "wniosek-nominacja", component: () => import("@/views/NominationRequestView.vue"), meta: { noAssistant: true } },
      { path: "ustawienia/hierarchia", name: "hierarchia", component: () => import("@/views/SettingsHierarchyView.vue"), meta: { noAssistant: true } },
      { path: "ustawienia/powiadomienia", name: "settings-powiadomienia", component: () => import("@/views/SettingsNotificationsView.vue") },
      { path: "ustawienia/moj-telefon", name: "settings-moj-telefon", component: () => import("@/views/SettingsMySmsView.vue") },
      { path: "ustawienia/integracje", name: "settings-integracje", component: () => import("@/views/SettingsIntegrationsView.vue"), meta: { noAssistant: true } },
      { path: "ustawienia/szablon-spotkania", name: "settings-szablon-spotkania", component: () => import("@/views/SettingsMailTemplateView.vue"), meta: { noAssistant: true } },
      { path: "ustawienia/tokeny", name: "settings-tokeny", component: () => import("@/views/SettingsTokensView.vue"), meta: { adminOnly: true } },
      { path: "ustawienia/nadawcy", name: "settings-nadawcy", component: () => import("@/views/SettingsSendersView.vue"), meta: { adminOnly: true } },
      { path: "ustawienia/stawki", name: "settings-stawki", component: () => import("@/views/SettingsRatesView.vue"), meta: { adminOnly: true } },
      { path: "ustawienia/katalog", name: "admin-katalog", component: () => import("@/views/AdminCatalogView.vue"), meta: { adminOnly: true } },
      { path: "ustawienia/oferty", name: "admin-oferty", component: () => import("@/views/AdminOffersView.vue"), meta: { adminOnly: true } },

      // Admin (view_permission=global only — backend wymusza + FE route guard, patrz beforeEach)
      { path: "ustawienia/zespol", name: "admin-zespol", component: () => import("@/views/AdminUsersView.vue"), meta: { adminOnly: true } },
      { path: "ustawienia/audyt", name: "admin-audyt", component: () => import("@/views/AdminAuditView.vue"), meta: { adminOnly: true } },
      { path: "ustawienia/pola", name: "admin-pola", component: () => import("@/views/AdminCustomFieldsView.vue"), meta: { adminOnly: true } },
      { path: "ustawienia/tagi", name: "admin-tagi", component: () => import("@/views/AdminTagsView.vue"), meta: { adminOnly: true } },

      // Hub administracyjny — jedna strona z kartami do powyższych (link w ikonce profilu, R2 reorg).
      { path: "admin", name: "admin-hub", component: () => import("@/views/AdminHubView.vue"), meta: { adminOnly: true } },
      // Raport interwencji OVB, S1 (docs/PLAN-serwis.md) — tylko admin; moduł za flagą INTERVENTIONS_ENABLED.
      { path: "serwis/importy", name: "serwis-importy", component: () => import("@/views/InterventionImportsView.vue"), meta: { adminOnly: true } },
      { path: "serwis/importy/:id", name: "serwis-import", component: () => import("@/views/InterventionImportDetailView.vue"), meta: { adminOnly: true } },
      { path: "serwis/numery", name: "serwis-numery", component: () => import("@/views/InterventionAgentNumbersView.vue"), meta: { adminOnly: true } },

      // Kreator onboarding — ustalenie celu zarobkowego i zapisa jako goal (S2-10).
      { path: "onboarding", name: "onboarding", component: () => import("@/components/onboarding/OnboardingWizard.vue"), meta: { noAssistant: true } },
      // Widok dyrektora — plan zespołu vs realizacja (S4-7).
      { path: "dyrektor", name: "dyrektor", component: () => import("@/views/DirectorView.vue"), meta: { noAssistant: true } },
    ],
  },
  // Portal klienta — PUBLICZNY (token w URL + hasło od doradcy; własny JWT portalowy).
  { path: "/portal/:token", name: "portal", component: () => import("@/views/PortalView.vue"), meta: { public: true } },
  // Druk oferty — pełna strona bez shell (czysty wydruk/PDF).
  { path: "/oferty/:id/print", name: "oferta-print", component: () => import("@/views/OfferPrintView.vue"), props: true },
  // Druk ilustracji oszczędności (wersja uproszczona = str. 1 / pełna = 2 strony, wariant wybierany w toolbarze).
  { path: "/oferty/:id/print-oszczednosci", name: "oferta-oszczednosci-print", component: () => import("@/views/SavingsOfferPrintView.vue"), props: true },
  // Druk karty rekrutacyjnej (5 stron A4: okładka, obszary, model zarobków segmentu, strona segmentowa, narzędzia+CTA).
  { path: "/oferty/:id/print-rekrutacja", name: "oferta-rekrutacja-print", component: () => import("@/views/RecruitOfferPrintView.vue"), props: true },
  // Finansowy check-up — dokument scalający APK + umowy z systemu (z karty osoby).
  { path: "/analiza/:personId/print", name: "checkup-print", component: () => import("@/views/CheckupPrintView.vue"), props: true },
  // Druk broszury marketingowej produktu (dane doradcy z profilu; ?klient= → wersja imienna).
  { path: "/broszury/:code/print", name: "broszura-print", component: () => import("@/views/BrochurePrintView.vue"), props: true },
  // Przewodnik „Jak liczyć prowizję: życie, majątek, punkty" — 3 strony A4, liczby z silnika (commission-guide.gen.json).
  { path: "/druk/przewodnik-prowizje", name: "przewodnik-prowizje-print", component: () => import("@/views/CommissionGuidePrintView.vue"), meta: { noAssistant: true } },
  // Wniosek na nominację — 1 strona A4 do podpisu (szkic z formularza w sessionStorage).
  { path: "/druk/wniosek-nominacja", name: "wniosek-nominacja-print", component: () => import("@/views/NominationPrintView.vue"), meta: { noAssistant: true } },
  { path: "/:catchAll(.*)", redirect: "/szanse" },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Po wdrożeniu karta otwarta wcześniej szuka plików ekranów o starych nazwach (nowy build = nowe
// hashe) → dynamiczny import pada, a kliknięcie w menu „nic nie robi" (zgłoszenie Marcina
// 2026-09-23). Przeładuj na docelowy adres — raz na minutę na ścieżkę, żeby nie zapętlić, gdyby
// plik brakował naprawdę.
const CHUNK_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i;
router.onError((err, to) => {
  if (!CHUNK_ERROR.test(String((err as Error)?.message ?? err))) return;
  const key = `chunk-reload:${to.fullPath}`;
  try {
    const last = Number(sessionStorage.getItem(key) ?? 0);
    if (Date.now() - last < 60_000) return;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    /* brak sessionStorage — przeładuj i tak */
  }
  window.location.assign(to.fullPath);
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (!auth.initialChecked) await auth.initFromSession();
  if (to.meta.public) {
    if (auth.isAuthenticated && to.name === "login") return { name: "szanse" };
    return true;
  }
  if (!auth.isAuthenticated) {
    return { name: "login", query: { redirect: to.fullPath } };
  }
  // Trasy admin tylko dla view=global (wcześniej shell admin renderował się każdemu — Bug #2 smoke).
  if (to.meta.adminOnly && (auth.scope !== "global" || auth.isAssistant)) {
    return { name: "szanse" };
  }
  if (to.meta.noAssistant && auth.isAssistant) {
    return { name: "zlecenia" };
  }
  return true;
});
